<script setup lang="ts">
import { computed, ref } from "vue";
import {
  CheckCircle2,
  EyeOff,
  FileClock,
  MessageSquareText,
} from "lucide-vue-next";
import { useWorkspaceStore } from "@/stores/workspace";
import PersonAvatar from "@/components/people/PersonAvatar.vue";

const workspace = useWorkspaceStore();
const filter = ref<"all" | "internal">("all");
const activity = computed(() =>
  (workspace.active?.activity || []).filter(
    (item) =>
      filter.value === "all" || item.visibility.toLowerCase() === filter.value,
  ),
);
</script>

<template>
  <div class="activity-page">
    <header>
      <div>
        <h2>Project activity</h2>
        <p>Material changes and conversations in the work you can access.</p>
      </div>
      <label>
        <span class="sr-only">Filter project activity</span>
        <select v-model="filter" class="select">
          <option value="all">All activity</option>
          <option value="internal">Internal activity</option>
        </select>
      </label>
    </header>
    <section class="activity-sheet">
      <div class="activity-date">Today</div>
      <article
        v-for="(item, index) in activity"
        :key="item.id"
        class="activity-item"
      >
        <span class="activity-marker"
          ><MessageSquareText v-if="index === 0" :size="16" /><FileClock
            v-else-if="index === 3"
            :size="16" /><CheckCircle2 v-else :size="16" /></span
        ><PersonAvatar
          :name="item.actor"
          :initials="item.initials"
          :photo-url="item.photoUrl"
        />
        <div>
          <p>
            <strong>{{ item.actor }}</strong> {{ item.action }}
            <strong>{{ item.context }}</strong>
          </p>
          <small
            >{{ item.timestamp }}
            <EyeOff
              v-if="item.visibility === 'Internal'"
              :size="13"
              aria-label="Internal — visible only to VIPdesk project participants"
              title="Internal — visible only to VIPdesk project participants"
            />
          </small>
        </div>
      </article>
    </section>
  </div>
</template>

<style scoped>
.activity-page {
  height: 100%;
  overflow: auto;
  padding: 24px 28px 50px;
  background: var(--paper);
}
.activity-page > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}
.activity-page header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.activity-sheet {
  max-width: 900px;
  margin-top: 20px;
  padding: 0 24px 28px;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: white;
}
.activity-date {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 17px 0 10px;
  border-bottom: 1px solid var(--rule);
  background: white;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}
.activity-item {
  position: relative;
  min-height: 78px;
  display: grid;
  grid-template-columns: 28px 32px 1fr;
  align-items: flex-start;
  gap: 10px;
  padding: 18px 0;
  border-bottom: 1px solid var(--rule);
}
.activity-item:last-child {
  border-bottom: 0;
}
.activity-item::before {
  content: "";
  position: absolute;
  left: 13px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--rule);
}
.activity-marker {
  position: relative;
  z-index: 1;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 1px solid var(--rule);
  border-radius: 50%;
  color: var(--shared);
  background: white;
}
.activity-item p {
  margin-top: 3px;
}
.activity-item small {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  color: var(--text-soft);
}
.activity-item small svg {
  color: var(--internal);
}
@media (max-width: 600px) {
  .activity-page {
    padding: 18px 14px 40px;
  }
  .activity-page > header {
    flex-direction: column;
  }
  .activity-sheet {
    padding-inline: 14px;
  }
  .activity-item {
    grid-template-columns: 28px 1fr;
  }
  .activity-item > .avatar {
    display: none;
  }
}
</style>
